import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as _ from 'underscore';

import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';

/**
* This is a react component that is used to instantiate angular directives. The purpose of this component is to
* allow angular directives to be ported to React one at a time.
*/
export class AngularDirective extends React.Component<AngularDirective.Props, any> implements React.ComponentLifecycle<AngularDirective.Props, any> {

  public static element = React.createFactory(AngularDirective);

  constructor(props: AngularDirective.Props, context: any) {
    super(props, context);
  }

  public componentDidMount() {
    this.updateFromProps();
  }

  public componentDidUpdate(prevProps: AngularDirective.Props) {
    this.updateFromProps(prevProps);
  }

  public render() {
    return React.createElement('span', {}, this.props.children);
  }

  public componentWillUnmount() {
    // If we don't remove the element we created using JQuery remove,
    // then we'll leak some memory when React removes it.
    const element = $(ReactDOM.findDOMNode(this));
    if (this.props.removeChildren) {
      element.children().remove();
    } else {
      element.remove();
    }
  }

  /**
  * updates the DOM element based on the value of current props. Uses prevProps to determine if the things that have changed
  * require angular compilation of the element or just a digest cycle.
  */
  private updateFromProps(prevProps: AngularDirective.RenderedProps = {}) {
    let shouldCompile = false;
    const currentProps = this.props;

    const element = $(ReactDOM.findDOMNode(this));
    const attrs = currentProps.attributes || {};
    _.each(attrs, (value: string, key: string) => {
      if (!prevProps.attributes || !prevProps.attributes[key]) {
        // if we haven't seen this attribute directive before, it will need to be compiled
        shouldCompile = true;
      }
      element.attr(key, value);
    });

    // any attributes that have been removed should be removed from the element
    const attributesToRemove = _.difference(_.keys(prevProps.attributes), _.keys(currentProps.attributes));
    attributesToRemove.map((key) => {
      element.removeAttr(key);
    });

    if (currentProps.template && currentProps.template !== prevProps.template) {
      // template was updated or is new
      shouldCompile = true;
      element.empty();
      element.append(currentProps.template);
    } else if (prevProps.template && !currentProps.template) {
      // template was removed
      element.empty();
    }

    if (shouldCompile) {
      this.compileElement();
    }
    this.digest();
  }

  private compileElement() {
    const angularContext = this.props.angularContext;
    const element = $(ReactDOM.findDOMNode(this) as HTMLElement);
    angularContext.compileElement(element, this.props.setupScope);
  }

  private digest() {
    const angularContext = this.props.angularContext;
    angularContext.enqueueDigest();
  }

}

export module AngularDirective {

  /**
  * Properties:
  *
  * setupScope: an optional function parameter that can be used to setup the scope before the directive is compiled
  * attributes:
  *   An object containing key:value pairs which will be assigned to the DOM Element as attributes and values.
  *   You would use this to instantiate attribute directives.
  *   Required if 'template' is not used.
  * template:
  *   a string containing markup which will be compiled by angular. This would be used to instantiate element
  *   directives. Example: "<tb:permissions-panel />". Required if 'attributes' is not used.
  */
  export interface Props extends ReactDirectiveProps, RenderedProps {
    angularContext: AngularContext;
    removeChildren?: boolean;
    setupScope?(scope: ng.IScope): ng.IScope;
  }

  export interface RenderedProps  {
    attributes?: _.Dictionary<string>;
    template?: string;
  }

}
